/*
 * Copyright (C) 2016 - 2024 Vivien Pelenc / CEFE / CNRS
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */


import React, {useEffect, useRef, useState} from 'react';
import Chart from 'chart.js/auto';
import {getCumul, getMeteo24hData, getMeteoDataDate} from "../ConnectionBDD";

export default function ChartRadarWind() {
    //const color = 'rgb(227,132,0)';
    const [data, setdata] = useState([]);
    const [labels, setKeys] = useState([]);
    const [dateDebut, setDateDebut] = useState(new Date(Date.now()));
    const [dateFin, setDateFin] = useState(new Date(Date.now()));
    const [depth, setDepth] = useState(0);


    useEffect(() => {
        const fetchData = async () => {
            try {
                let response;
                switch (depth) {
                    case 0:

                        response = await getMeteoDataDate(
                            new Date(dateDebut).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                            }),
                            new Date(dateFin).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                            }));
                        break;
                    case 1:
                        response = await getMeteo24hData();
                        break;
                }
                const sortedResponse = Object.values(response).sort((a, b) => {
                    // Comparer les ID de mesure
                    return a.id_mesure - b.id_mesure;
                });


                // Transformez la réponse en tableau
                const dataArray = sortedResponse.map(value => value.wind_speed);
                setdata(dataArray);
                //const keysArray = sortedResponse.map(value => value.time);
                const keysArray = sortedResponse.map(value => value.wind_dir);

                setKeys(keysArray);

            } catch (error) {
                console.error('Erreur lors de la récupération des données :', error);
            }
        };


        fetchData();

        const refreshData = () => {
            const currentDate = new Date();
            const minutes = currentDate.getMinutes();

            // Si l'heure est xxh01 ou xxh31, actualisez les données
            if (minutes === 1 || minutes === 31) {
                fetchData();
            }
        };

        // Actualisez les données toutes les secondes
        const interval = setInterval(refreshData, 59000);

        // Nettoyez l'intervalle lors du démontage du composant
        return () => clearInterval(interval);

    }, [dateDebut, dateFin]);

    return (
        <div>
            <Graph labels={labels} data={data} setDateDebut={setDateDebut} dateDebut={dateDebut} setDateFin={setDateFin}
                   dateFin={dateFin}/>
        </div>
    );
}

function Graph({labels, data, color, setDateDebut, dateDebut, setDateFin, dateFin}) {
    const canvasRef = useRef(null);
    const chartRef = useRef(null);
    let resizeTimeout = null;

    useEffect(() => {
        const resizeChart = () => {
            if (chartRef.current) {
                chartRef.current.resize();
            }
        };

        const delayedResize = () => {
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
            resizeTimeout = setTimeout(resizeChart, 100); // Redimensionner après 0.5 sec d'inactivité
        };

        window.addEventListener('resize', delayedResize);

        return () => {
            window.removeEventListener('resize', delayedResize);
            if (chartRef.current) {
                chartRef.current.destroy();
            }
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
        };
    }, []);
    useEffect(() => {




        // Fonction pour arrondir un nombre à la dizaine supérieure
        function roundUpToNextTen(num) {
            return Math.ceil(num / 10) * 10;
        }

        // Fonction pour arrondir un nombre à la l'unité inférieure
        function roundDownToUnit(num) {
            return Math.floor(num);
        }


        const svg = document.getElementById('circle');
        const centerX = 120;
        const centerY = 120;

        //Clear du svg
        while (svg.firstChild) {
            svg.removeChild(svg.firstChild);
        }

        // Fonction pour créer les segments de cercle
        function createCircleSegments(svg,radius, part,colors) {

            let nbPart = 32;
            const k = (360 / nbPart);

            part = 22+(part*2)
            if(part>32){
                part=part-32
            }

            for (let i = 0; i < nbPart; i++) {
                if (i === part || ((i+1) === part) || ((part === 32 && i === 0)) ){
                    const startAngle = (i * k) * (Math.PI / 180); // Convertir en radians
                    const endAngle = ((i + 1) * k) * (Math.PI / 180);

                    const x1 = centerX + radius * Math.cos(startAngle);
                    const y1 = centerY + radius * Math.sin(startAngle);
                    const x2 = centerX + radius * Math.cos(endAngle);
                    const y2 = centerY + radius * Math.sin(endAngle);

                    const largeArcFlag = (endAngle - startAngle) > Math.PI ? 1 : 0; // Déterminer si l'arc est grand ou petit

                    const pathData = `M ${centerX},${centerY} L ${x1},${y1} A ${radius},${radius} 0 ${largeArcFlag},1 ${x2},${y2} Z`;

                    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
                    path.setAttribute("d", pathData);
                    path.setAttribute("fill", colors);

                    svg.appendChild(path);
                }
            }
        }

        let directions = labels;
        let vitesse = data;
        //let directions = [4,4,4,4,4,4,4,4,1];
        //let vitesse = [8.1,10.1,8.1,10.1,12.1,8.1,10.1,12.11,1];


        //console.log(directions);
        //console.log(vitesse);


        // Trouver la direction avec le plus d'occurrences
        let occurrences = {};
        let directionMoreOcurence = null;
        let maxCount = 0;

        for (let direction of directions) {
            if (direction != null) {
                if (occurrences[direction]) {
                    occurrences[direction] += 1;
                } else {
                    occurrences[direction] = 1;
                }
            }
        }

        for (let direction in occurrences) {
            if (occurrences[direction] > maxCount) {
                maxCount = occurrences[direction];
                directionMoreOcurence = direction;
            }
        }

        console.log("Direction avec le plus d'occurrences : " + directionMoreOcurence + " (" + maxCount + " occurrences)");




        let lengthVitesse = vitesse.length;
        let lengthDirection = directions.length;

        let palier =[38,20,12,6,0];
        let color = ["#A12208","#EEA614","#12EA30","#11D6FD","#119DFD"]


        let echelle = (maxCount*100/lengthDirection)/10;
        echelle = Math.ceil(echelle);

        //console.log(maxCount*100+" / "+lengthDirection);
        //console.log(echelle+"<-- Echelle");

        // Créer les segments en fonction de la direction et de la vitesse
        let count = 0;
        for (let i = 0; i < 16; i++) {
            for (let j = palier.length-1; j >= 0; j--) {
                count = 0
                for (let l = 0; l < lengthDirection; l++) {
                    if (directions[l] === i && vitesse[l] >= palier[j]) {
                        count++
                    }
                }
                //"40*100" passe de 0:100 a 0:Echelle
                createCircleSegments(svg,(count*100/ lengthDirection)/(echelle*10)*100,i+1,color[j]);

                //if (j===4){console.log((count*100/ lengthDirection)/40*100);}
            }
        }


        // Ajouter les directions N, NE, E, SE, S, SO, O, NO
        const directionsRose = ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'];
        const directionAngles = [270, 315, 0, 45, 90, 135, 180, 225]; // Angles pour N, NE, E, SE, S, SW, W, NW

        for (let i = 0; i < directionsRose.length; i++) {
            const angle = directionAngles[i] * (Math.PI / 180); // Convertir en radians
            const textX = centerX + (110 ) * Math.cos(angle); // Position X
            const textY = centerY + (110 ) * Math.sin(angle); // Position Y

            const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
            text.setAttribute("x", textX);
            text.setAttribute("y", textY);
            text.setAttribute("text-anchor", "middle"); // Centrer le texte
            text.setAttribute("dominant-baseline", "middle"); // Centrer verticalement
            text.setAttribute("font-size", "10");
            text.setAttribute("fill", "black");
            text.textContent = directionsRose[i];

            svg.appendChild(text);
        }

        // Ajouter les cercles concentriques
        const circleRadii = [];
        const circleRadii2 = [];
        let jIndice = 0;

        for (let i = 0; i < echelle; i++) {
            circleRadii.push((i+1)*100/echelle);

        }
        //console.log(echelle);



        circleRadii.forEach(r => {
            const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
            circle.setAttribute("cx", centerX);
            circle.setAttribute("cy", centerY);
            circle.setAttribute("r", r);
            circle.setAttribute("stroke", "black");
            circle.setAttribute("stroke-width", "1");
            circle.setAttribute("fill", "none");
            svg.appendChild(circle);
        });

        circleRadii2.forEach(r => {
            const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
            circle.setAttribute("cx", centerX);
            circle.setAttribute("cy", centerY);
            circle.setAttribute("r", r);
            circle.setAttribute("stroke", "black");
            circle.setAttribute("stroke-width", "0.15");
            circle.setAttribute("fill", "none");
            svg.appendChild(circle);
        });


        //legende
        const legende = document.createElementNS("http://www.w3.org/2000/svg", "path");
        const pathData = `M 125,229 L 220.5,229`
        legende.setAttribute("d", pathData);
        legende.setAttribute("stroke", "black");
        legende.setAttribute("stroke-width", "1");
        svg.appendChild(legende);


        //echelle
        for (let i = 0; i < echelle; i++) {
            const legende = document.createElementNS("http://www.w3.org/2000/svg", "path");
            const pathData = `M `+(((i+1)*100/echelle)+120)+`,229 L `+ (((i+1)*100/echelle)+120)+`,232`
            legende.setAttribute("d", pathData);
            legende.setAttribute("stroke", "black");
            legende.setAttribute("stroke-width", "1");
            svg.appendChild(legende);

            const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
            text.setAttribute("x", ((i+1)*100/echelle)+120);
            text.setAttribute("y", 238);
            text.setAttribute("text-anchor", "middle"); // Centrer le texte
            text.setAttribute("dominant-baseline", "middle"); // Centrer verticalement
            text.setAttribute("font-size", "6");
            text.setAttribute("fill", "black");
            text.textContent = ((i+1)*10)+"%";
            svg.appendChild(text);

        }

        //couleur

        const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
        text.setAttribute("x", 277);
        text.setAttribute("y", 10);
        text.setAttribute("text-anchor", "middle"); // Centrer le texte
        text.setAttribute("dominant-baseline", "middle"); // Centrer verticalement
        text.setAttribute("font-size", "10");
        text.setAttribute("fill", "black");
        text.textContent = ("Vitesse (km/h)");
        svg.appendChild(text);

        for (let i = 0; i < palier.length; i++) {
            const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
            rect.setAttribute("x", 240);
            rect.setAttribute("y", 17 + (i*12));
            rect.setAttribute("width", 20);
            rect.setAttribute("height", 10);
            rect.setAttribute("stroke", "black");
            rect.setAttribute("stroke-width", 0.5);
            rect.setAttribute("fill", color[i]);
            // Ajout du rectangle au SVG
            svg.appendChild(rect);

            const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
            text.setAttribute("x", 284);
            text.setAttribute("y", 22 + (i*12));
            text.setAttribute("text-anchor", "middle"); // Centrer le texte
            text.setAttribute("dominant-baseline", "middle"); // Centrer verticalement
            text.setAttribute("font-size", "10");
            text.setAttribute("fill", "black");
            if (i == 0){
                text.textContent = (">= " + palier[i]);
            } else {
                if (i == palier.length-1 ){
                    text.textContent = ("<= " + (palier[i-1]-1));
                } else {
                    text.textContent = (palier[i]+" - "+ (palier[i-1]-1));
                }
            }
            svg.appendChild(text);
        }



        //titre
        const titre = document.createElementNS("http://www.w3.org/2000/svg", "text");
        titre.setAttribute("x", 139);
        titre.setAttribute("y", -10);
        titre.setAttribute("text-anchor", "middle"); // Centrer le texte
        titre.setAttribute("dominant-baseline", "middle"); // Centrer verticalement
        titre.setAttribute("font-size", "10");
        titre.setAttribute("fill", "black");
        titre.setAttribute("font-weight", "bold");
        titre.textContent = ("Direction et vitesse des vents (fréquence sur 24h)");
        svg.appendChild(titre);



        //Affichage de la plus grande rafale
        let maxVitesse = Math.round(Math.max(...vitesse));
        if (maxVitesse === -Infinity) {
            maxVitesse = 0;
        }
        const maxRafale = document.createElementNS("http://www.w3.org/2000/svg", "text");
        maxRafale.setAttribute("x", 20);
        maxRafale.setAttribute("y", 250);
        maxRafale.setAttribute("text-anchor", "left"); // Centrer le texte
        maxRafale.setAttribute("dominant-baseline", "middle"); // Centrer verticalement
        maxRafale.setAttribute("font-size", "10");
        maxRafale.setAttribute("fill", "black");
        maxRafale.textContent = ("Rafale la plus forte : "+maxVitesse+" km/h");
        svg.appendChild(maxRafale);

        // Force de Beaufort
        let force = 0;
        // Dernier élément de vitesse
        let last = maxVitesse;

        if (last <= 1) {
            force = 0;
        } else if (last >= 1 && last <= 5) {
            force = 1;
        } else if (last >= 6 && last <= 11) {
            force = 2;
        } else if (last >= 12 && last <= 19) {
            force = 3;
        } else if (last >= 20 && last <= 28) {
            force = 4;
        } else if (last >= 29 && last <= 38) {
            force = 5;
        } else if (last >= 39 && last <= 49) {
            force = 6;
        } else if (last >= 50 && last <= 61) {
            force = 7;
        } else if (last >= 62 && last <= 74) {
            force = 8;
        } else if (last >= 75 && last <= 88) {
            force = 9;
        } else if (last >= 89 && last <= 102) {
            force = 10;
        } else if (last >= 103 && last <= 117) {
            force = 11;
        } else if (last >= 118) {
            force = 12;
        }

        const beaufort = document.createElementNS("http://www.w3.org/2000/svg", "text");
        beaufort.setAttribute("x", 20);
        beaufort.setAttribute("y", 265);
        beaufort.setAttribute("text-anchor", "left"); // Centrer le texte
        beaufort.setAttribute("dominant-baseline", "middle"); // Centrer verticalement
        beaufort.setAttribute("font-size", "10");
        beaufort.setAttribute("fill", "black");
        beaufort.textContent = ("Force de Beaufort : "+force);
        svg.appendChild(beaufort);



    }, [labels, data]);
    useEffect(() => {
        const handleResize = () => {
            if (chartRef.current) {
                chartRef.current.resize();
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
            <svg

                id="circle"
                width="330"
                height="330"
                viewBox="0 0 330 260"
                style={{ flexShrink: 0 }}
            ></svg>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                <div>
                    <label htmlFor="start-date">Date de début :</label>
                    <input
                        type="date"
                        id="start-date"
                        value={getFormattedDate(dateDebut)}
                        onChange={(e) => setDateDebut(new Date(e.target.value))}
                    />
                </div>

                <div>
                    <label htmlFor="end-date">Date de fin :</label>
                    <input
                        type="date"
                        id="end-date"
                        value={getFormattedDate(dateFin)}
                        onChange={(e) => setDateFin(new Date(e.target.value))}
                    />
                </div>

                <div style={{ display: 'flex', paddingTop: '10px' }}>
                    <button onClick={() => {
                        setDateDebut(new Date(dateDebut.getTime() - 86400000));
                        setDateFin(new Date(dateFin.getTime() - 86400000));
                    }}>
                        &lt;&lt;
                    </button>

                    <button onClick={() => {
                        setDateDebut(new Date());
                        setDateFin(new Date());
                    }}>
                        Réinitialiser
                    </button>


                    <button
                        style={{ visibility: (dateFin.getTime() + 86400000) < Date.now() ? 'visible' : 'hidden' }}
                        onClick={() => {
                            setDateDebut(new Date(dateDebut.getTime() + 86400000));
                            setDateFin(new Date(dateFin.getTime() + 86400000));
                        }}
                    >
                        &gt;&gt;
                    </button>

                </div>
            </div>
        </div>
    );

}



function getFormattedDate(date) {
    // Utiliser toLocaleDateString pour obtenir une date au format jour/mois/année
    const dateParts = date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    }).split('/');

    // Réorganiser les parties pour obtenir le format YYYY-MM-DD
    const day = dateParts[0];
    const month = dateParts[1];
    const year = dateParts[2];

    return `${year}-${month}-${day}`;
}